import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { format } from 'date-fns';
import { DATE_FORMAT } from '@cyberint/common-utils';
import { noop } from 'lodash';
import Icons, { IconSizes } from '../Icons';
import Select from '../Select/Select';
import DateRangePicker from './DateRangePicker';
import { mapDateOptionToRange, Orientation } from './consts';
import * as S from './styled';

const RANGE_OPTION = 'range';

const DateRangeSelectInline = ({
  value,
  onBlur,
  onChange,
  options,
  dateFormat = DATE_FORMAT.MMM_DD_YYYY_FORMAT,
  addCustomOption = true,
  disabled,
  clearable,
  width,
  height,
  label,
  testId
}) => {
  const [isOpen, setOpen] = useState(false);
  const onRangeChange = ([from, to]) => {
    onChange({ from, to, option: { value: RANGE_OPTION } });
  };
  const rangeOption = {
    value: RANGE_OPTION,
    className: 'dsc-range-option',
    label: (
      <DateRangePicker
        testId={testId && `${testId}-RangePicker`}
        onChange={onRangeChange}
        orientation={Orientation.VER}
        showConnector={false}
        dateFormat={dateFormat}
        onClose={() => {
          setOpen(false);
        }}
        value={[value?.from, value?.to]}
      />
    )
  };

  const handleChange = e => {
    const selected = e.target.value;
    if (selected === RANGE_OPTION) {
      e.stopPropagation();
      return;
    }
    setOpen(false);
    const mappedValue = mapDateOptionToRange(selected, options);
    onChange(mappedValue);
  };

  const onOpen = () => setOpen(true);

  const onSpecialSelect = e => {
    /** use-case where menu is opened and user want to close the menu without selecting and option.
     * we need to detect modal backdrop and close it, but still not to close on (other) clicks on date-range elements */
    const targetClassname = e?.target?.className;
    if (typeof targetClassname === 'string' && targetClassname?.includes('MuiBackdrop-root')) {
      setOpen(false);
    }
  };

  const selectedValue = value?.option?.value || value;

  return (
    <Select
      onChange={handleChange}
      options={addCustomOption ? [...options, rangeOption] : options}
      value={selectedValue}
      width={width}
      height={height}
      label={label}
      testId={testId}
      onBlur={onBlur}
      disabled={disabled}
      clearable={clearable}
      renderValue={() => {
        const renderedVal =
          selectedValue === RANGE_OPTION
            ? `${value?.from && format(value.from, dateFormat)} - ${value?.to && format(value.to, dateFormat)}`
            : options.find(o => o.value === selectedValue)?.label;
        return (
          renderedVal != null && (
            <S.SelectPlaceholder>
              <Icons.Calendar fontSize={IconSizes.S} />
              <div>{renderedVal}</div>
            </S.SelectPlaceholder>
          )
        );
      }}
      onOpen={onOpen}
      onClose={noop}
      onClick={onSpecialSelect}
      open={isOpen}
    />
  );
};

DateRangeSelectInline.propTypes = {
  /* provides selected value: { from, to } */
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.node.isRequired,
      value: PropTypes.any.isRequired,
      unit: PropTypes.string.isRequired,
      amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
    })
  ).isRequired,
  value: PropTypes.shape({
    from: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    to: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    option: PropTypes.any
  }).isRequired,
  disabled: PropTypes.bool,
  clearable: PropTypes.bool,
  addCustomOption: PropTypes.bool,
  width: PropTypes.string,
  height: PropTypes.oneOf(['LARGE', 'MEDIUM', 'SMALL', 'TINY', PropTypes.string, PropTypes.number]),
  label: PropTypes.string,
  dateFormat: PropTypes.string,
  testId: PropTypes.string
};

export default DateRangeSelectInline;
